import { createPasswordPolicy, createPasswordPolicySchema } from '@acme/b2b/security';
import { NoticesComponentModule } from '@b2b/common/components/notices';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { CapitalizeFirstLetterFilterModule } from '@b2b/common/filters';
import { ApiService, CustomerFeeConfig, RateCard } from '@b2b/common/services/api/types';
import { AuthenticationService, AuthenticationServiceModule } from '@b2b/common/services/auth/authentication.service';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { ValidateService } from '@b2b/common/services/validate';
import { ensureArray } from '@b2b/common/utils/coerce-array';
import { ACMEResponse, UIBModalService } from '@b2b/types/core';
import { LooseAutocomplete, TODO } from '@b2b/types/core/utils';
import uiRouter from '@uirouter/angularjs';
import angular, { IFilterService, ILocationService, ISCEService, IScope, ITimeoutService } from 'angular';
import * as z from 'zod';
import {
  AccountAdministrationService,
  AccountAdministrationServiceModule,
  CCValidationSettings,
  CCValidationSettingsOptions,
  EmailTemplate,
  EmailTemplateMarshall,
} from '../services/account-administration.service';
import {
  ACCOUNT_ADMINISTRATION_ERROR_CODES,
  ACCOUNT_ADMINISTRATION_SENDGRID_COMMON_MESSAGE,
  ACCOUNT_ADMINISTRATION_SENDGRID_ERRORS,
  ACCOUNT_ADMINISTRATION_SENDGRID_INVALID_KEY,
  ACCOUNT_ADMINISTRATION_SENDGRID_INVALID_TEMPLATE,
} from './AccountAdministrationConstants';

AccountAdministrationController.$inject = [
  '$scope',
  '$sce',
  '$timeout',
  '$filter',
  '$uibModal',
  '$location',
  'ApiService',
  'NoticeFactory',
  'ConfigData',
  'EmailTemplatesData',
  'FeesData',
  'RateCardsData',
  'PermissionsService',
  'UserPreferencesService',
  'ThemesHelperService',
  'ValidateService',
  'AuthenticationService',
  'AccountAdministrationService',
  'EmailLanguagesService',
];

function AccountAdministrationController(
  $scope: IScope & { accountAdministration: TODO },
  $sce: ISCEService,
  $timeout: ITimeoutService,
  $filter: IFilterService,
  $uibModal: UIBModalService,
  $location: ILocationService,
  ApiService: ApiService,
  NoticeFactory: NoticeFactory,
  ConfigData: TODO,
  EmailTemplatesData: TODO,
  FeesData: { data: ACMEResponse<{ list: CustomerFeeConfig[] }> } | null,
  RateCardsData: { data: ACMEResponse<RateCard[]> } | null,
  PermissionsService: PermissionsService,
  UserPreferencesService: TODO,
  ThemesHelperService: ThemesHelperService,
  ValidateService: ValidateService,
  authenticationService: AuthenticationService,
  accountAdministrationService: AccountAdministrationService,
  EmailLanguagesService: TODO,
) {
  const defaultPasswordPolicy = createPasswordPolicy();
  const ctrl = {
    isCSR: false,
    tabs: [
      { name: 'web', label: 'Website (B2C)' },
      { name: 'b2b', label: 'Backoffice (B2B)' },
      { name: 'pos', label: 'Point of Sale (POS)' },
      { name: 'ac', label: 'Access Control (AC)' },
      { name: 'payment', label: 'Payment' },
      { name: 'fees', label: 'Fees' },
      { name: 'finance', label: 'Finance' },
      { name: 'security', label: 'Security' },
      { name: 'printers', label: 'Printers' },
      { name: 'email', label: 'Email' },
      { name: 'analytics', label: 'Analytics' },
      { name: 'integrations', label: 'Integrations' },
    ],

    model: {
      web: {
        membership: '',
        membershipVerificationLastName: '',
        membershipSales: '',
        membershipBenefitsExtension: 0,
        giftCertificates: '',
        coupons: '',
        billingAddress: '',
        sortTemplatesByDisplayOrder: false,
      },
      b2b: {
        settings: {
          b2b: {},
          web: {},
          pos: {},
          reseller: {},
        },
        b2bBillingAddress: '',
        lockAlwaysRequiredToUpdateRequiresLockObjects: false,
        lockExpiryInSeconds: 14400,
        auxiliaryMembership: false,
        allowTicketTransactionOnExpiredMemberships: false,
      },
      featuresConfig: {
        objectLock: {
          lockAlwaysRequiredToUpdateRequiresLockObjects: false,
          lockExpiryInSeconds: 14400,
        },
        customers: {
          enableCustomersOrgCheckout: false,
        },
        coupons: {
          enableCoupons: false,
          allowMultiple: 14400,
        },
      },
      payment: {
        allowAMEX: '',
        amexPaymentProcessor: '',
        paymentProcessor: '',
        expressURL: '',
        expressAccountID: '',
        expressAccountToken: '',
        expressApplicationID: '',
        expressAcceptorID: '',
        creditCardValidationSettings: {
          b2bValidation: 'None',
          b2bDisplay: 'Zipcode',
          b2cValidation: 'None',
          b2cDisplay: 'Zipcode',
          posValidation: 'None',
          posDisplay: 'Zipcode',
        },
      } as TODO,
      pos: {
        surveyFrequency: '',
        offlinePin: '',
        allowPrintingForExpiredMemberships: false,
        disableMembershipManage: true,
        enableCustomerInputOnPurchase: false,
        vouchersRequireOrganization: true,
      },
      ac: {
        membershipTicketTypeSelection: false,
      },
      fees: {
        id: null as number | null,
        active: false,
        name: '',
        percentage: '',
        fixed: '',
        inputLabel: '',
        tooltipLabel: '',
        presentmentType: 'OPTIONAL' as 'PRECHECK' | 'OPTIONAL',
        refundable: false,
      },
      onlineRateCards: [] as RateCard[],
      finance: {
        rounding: '',
        abbreviatedName: '',
      },
      security: {
        passwordSecurityPolicyEnabled: defaultPasswordPolicy.passwordSecurityPolicyEnabled,
        passwordMinimumLength: defaultPasswordPolicy.passwordMinimumLength,
        passwordUseMixedCase: defaultPasswordPolicy.passwordUseMixedCase,
        passwordMinimumDigits: defaultPasswordPolicy.passwordMinimumDigits,
        passwordMinimumSymbols: defaultPasswordPolicy.passwordMinimumSymbols,
        sessionExpiryInMillis: 864000000,
      },
      printers: {
        printers: [],
      },
      email: {
        enableSendGridTracking: true,
        notificationPreference: 'always',
        enableCustomSendGridEmail: false,
        enableCustomEmail: false,
        sendGridProductionKey: '',
        sendGridSandboxKey: '',
        emailTemplates: [],
      },
      analytics: {
        enableGoogleAnalyticsTracking: false,
        enableGtmTracking: false,
      },
      integrations: {
        externalSystemIntegration: 'raisersedge',
        enableExternalNotifications: false,
        allowUpgradeExternalMemberships: false,
        invalidateExtraneousMembershipCards: false,
      },
    },

    emailTemplates: [] as TODO[],
    templateNames: {},

    templateTypes: [
      { name: 'Order Confirmation', templateType: 'order', emailTemplateType: 'single' },
      { name: 'Order Confirmation (Donation Only)', templateType: 'donation', emailTemplateType: 'single' },
      { name: 'Order Refund', templateType: 'refund', emailTemplateType: 'single' },
      { name: 'Order Rebook', templateType: 'rebook', emailTemplateType: 'single' },
      { name: 'Digital Membership Card', templateType: 'membershipCards', emailTemplateType: 'single' },
      { name: 'Event Cancelation', templateType: 'eventCanceled', emailTemplateType: 'bulk' },
      { name: 'Event Reschedule', templateType: 'eventRescheduled', emailTemplateType: 'bulk' },
    ],

    emailType: [
      { id: 'acme', name: 'ACME Default' },
      { id: 'custom', name: 'Custom' },
    ],

    sendGridAcount: [{ id: 'acme', name: 'ACME' }],

    htmlTooltip: $sce.trustAsHtml(
      '<p>When more than one language is selected, B2B and POS checkout display a dropdown with these values to select one language per order.</p><p>Checkout using ACME APIs can send any of the values from the full list.</p>',
    ),
    selectedLanguages: [] as TODO[],
    primaryLanguage: false as TODO | false,

    notificationPreferenceSettings: [
      { name: 'always', label: 'Always' },
      { name: 'never', label: 'Never' },
      { name: 'ask', label: 'Ask to send' },
    ],

    externalSystemIntegrationSettings: [
      { name: 'raisersedge', label: 'External CRM' },
      { name: 'SalesForce', label: 'Salesforce' },
    ],

    sessionTimeout: {
      days: 10,
      hours: 0,
      minutes: 0,
      seconds: 0,
    },

    lockExpiration: {
      hours: 4,
      minutes: 0,
    } as TODO,

    showOfflinePinPass: false,
    saveType: '',
    state: 'web',
    errors: {} as Record<string, TODO> | false,
    tabErrors: {} as Record<string, TODO>,

    cache: {} as Record<string, TODO>,

    b2cPaymentFieldValidation: '',
    b2bPaymentFieldValidation: '',
    posPaymentFieldValidation: '',
    b2cPaymentFieldsOptional: false,
    b2bPaymentFieldsOptional: false,
    posPaymentFieldsOptional: false,

    paymentProcessors: ['Stripe', 'Express'],
    amexPaymentProcessors: ['Stripe', 'Express'],
    displayTypes: ['Yes', 'No', 'Optional'],
    paymentValidation: [
      { name: 'fullAddress', label: 'Require full address' },
      { name: 'zipOnly', label: 'Require Zip/Postal code only' },
      { name: 'none', label: "Don't require anything" },
    ],

    roundingRules: [
      { name: 'NoRounding', label: 'No Rounding' },
      { name: 'RoundUp', label: 'Round Up' },
      { name: 'RoundDown', label: 'Round Down' },
    ],

    printers: [
      { type: 'BOCALemur200dpi', label: 'BOCA Lemur 200dpi' },
      { type: 'BOCALemur300dpi', label: 'BOCA Lemur 300dpi' },
      { type: 'EpsonTMT88V', label: 'Epson TM-T88V' },
      { type: 'ZebraiMZ320', label: 'Zebra iMZ 320' },
    ],

    ccValidationSettings: {
      b2b: {
        type: 'b2b',
        label: 'Backoffice (B2B)',
        display: 'Zipcode',
        validation: 'None',
      },
      b2c: {
        type: 'b2c',
        label: 'Website (B2C)',
        display: 'Zipcode',
        validation: 'None',
      },
      pos: {
        type: 'pos',
        label: 'Point of Sale (POS)',
        display: 'None',
        validation: 'None',
        validationReadonly: true,
      },
    } satisfies CCValidationSettingsOptions,

    userSettings: UserPreferencesService.settings,

    sendGridProductionKey: '',
    sendGridSandboxKey: '',

    sendGridProductionKeyEmpty: true,
    sendGridSandboxKeyEmpty: true,
    isSendgridProdAPIKeyEdit: false,
    isSendgridSandAPIKeyEdit: false,

    vm: {
      maskedSendGridProductionKey: '',
      maskedSendGridSandboxKey: '',
    },

    fieldKeys: {
      sendgridProductionApiKey: 'sendgrid-production-api-key',
      sendgridSandboxApiKey: 'sendgrid-sandbox-api-key',
    },

    buildDate: function (format) {
      return format + ' [  ' + $filter('date')(new Date(), format) + ' ]';
    },

    notice: new NoticeFactory(),

    init: function () {
      ctrl.isCSR = PermissionsService.check('CSR');
      ctrl.setSendGridAcount(ThemesHelperService.getCompany('name'));
      ctrl.setEmailTemplates();

      if (ConfigData.data && ConfigData.data.success) {
        ctrl.success.init(ConfigData);
      } else {
        ctrl.error.init(ConfigData);
        ctrl.error.init(ConfigData);
      }

      if (FeesData?.data?.success) {
        const list = FeesData.data.payload?.list;
        if (list?.length) {
          ctrl.processFeesData(list[0]);
        }
      }

      if (RateCardsData?.data?.success && Array.isArray(RateCardsData.data.payload)) {
        ctrl.model.onlineRateCards = RateCardsData.data.payload
          .filter((rc) => rc.saleChannel === 'Online')
          .map((rc) => ({
            ...rc,
            displayBrand: rc.creditCardBrand
              ? rc.creditCardBrand.replace(/([a-z])([A-Z])/g, '$1 $2')
              : 'all other Card Brands',
          }));
      }

      ctrl.updateState();
      /** @see ENG-12405 */
      // ctrl.setCustomLanguags();

      $scope.$watch('accountAdministration.model.email.enableCustomSendGridEmail', function (n, o) {
        if (!n) {
          ctrl.model.email.sendGridProductionKey = '';
          ctrl.model.email.sendGridSandboxKey = '';
          ctrl.sendGridProductionKeyEmpty = true;
          ctrl.sendGridSandboxKeyEmpty = true;
          ctrl.isSendgridProdAPIKeyEdit = false;
          ctrl.isSendgridProdAPIKeyEdit = false;
        }
      });
    },

    setCustomLanguags: function () {
      EmailLanguagesService.getSelectedLanguages()
        .then((response) => ctrl.success.setCustomLanguags(response))
        .catch((response) => ctrl.error.setCustomLanguags(response));
    },

    selectPrimary: function () {
      if (ctrl.selectedLanguages.length) {
        ctrl.selectedLanguages.forEach((item) => {
          if (ctrl.primaryLanguage.code && item.code === ctrl.primaryLanguage.code) {
            item.primary = true;
          } else {
            item.primary = false;
          }
        });
      }
    },

    findTab: function (name) {
      let r: { tab: TODO; index: number } | false = false;

      for (let i = 0, ii = ctrl.tabs.length; i < ii; i++) {
        if (ctrl.tabs[i].name === name) {
          r = {
            tab: ctrl.tabs[i],
            index: i,
          };
          break;
        }
      }

      return r;
    },

    isState: function (state: string) {
      return ctrl.state === state;
    },

    isSSO: function () {
      return authenticationService.isSSO();
    },

    shouldShowSaveButton: function (state) {
      if (!ctrl.isState(state)) {
        return false;
      }

      if (state === 'payment' && !ctrl.isCSR) {
        return false;
      }

      return true;
    },

    updateState: function () {
      let buttonRightLabel = 'Update';
      const found = ctrl.findTab(ctrl.state);

      if (found) {
        ctrl.setSaveType(ctrl.state);
        buttonRightLabel = buttonRightLabel + ' ' + found.tab.label;

        ctrl.restoreSendGridApiKeysWhenErrorsArePresent();
      } else {
        ctrl.setSaveType('all');
      }

      const buttonRight = (() => {
        const result = {
          permissions: ['TENANT_ADMINISTRATION_UPDATE'],
          label: buttonRightLabel,
          action: () => {
            ctrl.update();
          },
        };

        if (ctrl.state === 'payment') {
          return ctrl.isCSR ? result : undefined;
        }

        return result;
      })();

      $scope.$emit('Application::contentTitleChange', {
        title: 'Administration',
        buttonRight: buttonRight,
        buttonLeft: {
          label: 'Cancel',
          action: () => {
            ctrl.reset();
          },
        },
      });
    },

    /**
     * We're sharing a model for the forms in all of the account administration section.
     * We can have errors while trying to save our SengGrid API keys on the 'email' tab.
     */
    restoreSendGridApiKeysWhenErrorsArePresent: function () {
      if (ctrl.getErrorMessage(ctrl.fieldKeys.sendgridProductionApiKey)) {
        ctrl.model.email.sendGridProductionKey = ctrl.vm.maskedSendGridProductionKey;
        ctrl.cache.sendGridProductionKey = ctrl.model.email.sendGridProductionKey;

        delete ctrl.errors[ctrl.fieldKeys.sendgridProductionApiKey];
      }

      if (ctrl.getErrorMessage(ctrl.fieldKeys.sendgridSandboxApiKey)) {
        ctrl.model.email.sendGridSandboxKey = ctrl.vm.maskedSendGridSandboxKey;
        ctrl.cache.sendGridSandboxKey = ctrl.model.email.sendGridSandboxKey;

        delete ctrl.errors[ctrl.fieldKeys.sendgridSandboxApiKey];
      }

      const noErrorsLeft = Object.keys(ctrl.errors || {}).length === 0;

      if (noErrorsLeft) {
        ctrl.notice.hideErrorMessages();
      }
    },

    reset: function () {
      ctrl.model = ctrl.process(ctrl.cache) as TODO;
    },

    setSaveType: function (value: LooseAutocomplete<'all'>) {
      ctrl.saveType = value;
    },

    hasTabErrors: function (tab) {
      return ctrl.tabErrors[tab];
    },

    getState: function (name) {
      ctrl.state = name;
      ctrl.updateState();
    },

    getTabTemplate: function (name) {
      return 'views/application/views/account/administration/' + name + '.tpl.html';
    },

    toggleOfflinePinPass: function () {
      ctrl.showOfflinePinPass = !ctrl.showOfflinePinPass;
    },

    convertPaymentSettings: function () {
      ctrl.model.payment.b2cPaymentFieldValidation = ctrl.b2cPaymentFieldValidation;
      ctrl.model.payment.b2bPaymentFieldValidation = ctrl.b2bPaymentFieldValidation;
      ctrl.model.payment.posPaymentFieldValidation = ctrl.posPaymentFieldValidation;

      if (ctrl.b2cPaymentFieldsOptional && ctrl.b2cPaymentFieldValidation !== 'none') {
        ctrl.model.payment.b2cPaymentFieldValidation += 'Optional';
      }

      if (ctrl.b2bPaymentFieldsOptional && ctrl.b2bPaymentFieldValidation !== 'none') {
        ctrl.model.payment.b2bPaymentFieldValidation += 'Optional';
      }

      if (ctrl.posPaymentFieldsOptional && ctrl.posPaymentFieldValidation !== 'none') {
        ctrl.model.payment.posPaymentFieldValidation += 'Optional';
      }
    },

    verify: function () {
      const processed = ctrl.marshall();
      ctrl.errors = false;
      ctrl.resetEmailTemplateNames();
      ctrl.notice.hideErrorMessages();

      if (processed.enableCustomSendGridEmail) {
        ApiService.administration.emailTemplates
          .verify(ctrl.sendGridVerifyMarshall())
          .then((response) => ctrl.success.verify(response))
          .catch((response) => ctrl.error.update(response));
      }
    },

    validatePasswordMinimumLengthErrors: function (): string[] | null {
      const passwordPolicySchema = createPasswordPolicySchema(ctrl.model.security);
      const parsedPasswordPolicy = passwordPolicySchema.safeParse(ctrl.model.security);
      const error = parsedPasswordPolicy.error ? z.flattenError(parsedPasswordPolicy.error) : null;
      return error?.fieldErrors?.passwordMinimumLength ?? null;
    },

    update: function () {
      const passwordMinimumLengthErrors = ctrl.validatePasswordMinimumLengthErrors();
      if (passwordMinimumLengthErrors) {
        const messages = passwordMinimumLengthErrors.map((error) => ({ message: error }));
        ctrl.notice.showErrorMessages(messages);

        return false;
      }

      const processed = ctrl.marshall();

      ctrl.resetEmailTemplateNames();

      ctrl.notice.hideSuccessMessages();
      ctrl.notice.hideErrorMessages();

      // EmailLanguagesService.updateSelectedLanguages(ctrl.selectedLanguages).then(ctrl.success.updateSelectedLanguage, ctrl.error.updateSelectedLanguage);

      if (ctrl.isInValidExtenion()) {
        ctrl.notice.showErrorMessages([
          { message: 'Invalid! Grace period must be a number greater than or equal to 0.' },
        ]);

        return false;
      }

      if (processed.enableCustomSendGridEmail && !ValidateService.validate(ctrl.getValidators())) {
        ctrl.notice.showErrorMessages([{ message: ACCOUNT_ADMINISTRATION_SENDGRID_COMMON_MESSAGE }]);
        ctrl.errors = ValidateService.getErrorHash();
        return false;
      } else {
        ctrl.errors = false;
      }

      if (ctrl.state === 'fees') {
        const fees = ctrl.model.fees;
        const feePayload = {
          name: fees.name,
          active: fees.active,
          percentage: fees.percentage,
          fixed: fees.fixed,
          inputLabel: fees.inputLabel,
          tooltipLabel: fees.tooltipLabel,
          presentmentType: fees.presentmentType,
          refundable: fees.refundable,
          type: 'COVER_MY_COSTS',
          salesChannels: [{ id: 1 }],
        };
        const feeCall = fees.id
          ? ApiService.customerFees.update(fees.id, feePayload as TODO)
          : ApiService.customerFees.create(feePayload as TODO);
        feeCall
          .then((res) => {
            ctrl.processFeesData(res.data.payload);
            ctrl.success.update(res);
          })
          .catch((response) => ctrl.error.update(response));
      } else if (ctrl.state === 'email') {
        const processedEmailTemplates = ctrl.emailTemplatesMarshall(processed.emailTemplates);
        ApiService.administration
          .update(processed)
          .then((res) => {
            const updateResponse = res;
            ApiService.administration.emailTemplates
              .update(processedEmailTemplates)
              .then((res) => {
                ctrl.success.update(updateResponse);
                ctrl.success.emailtemplates(res);
              })
              .catch((res) => {
                ctrl.processUpdateResponse(updateResponse);
                ctrl.error.emailtemplates(res);
              });
          })
          .catch((response) => ctrl.error.update(response));
      } else {
        ApiService.administration
          .update(processed)
          .then(ctrl.success.update)
          .catch((response) => ctrl.error.update(response));
      }
    },

    isProd: function () {
      return $location.host() === 'backoffice.acmeticketing.com';
    },

    getValidators: function (errorObj?: TODO) {
      const validators: TODO[] = [];
      const error = errorObj || false;
      const errorType = error ? error.type : false;
      const errorMessage = error ? error.message : '';
      const templateError = error ? errorMessage.split(': ') : [];
      let templateId = '';
      let templateErrorMessage = ACCOUNT_ADMINISTRATION_SENDGRID_INVALID_TEMPLATE;
      const isTemplateError = templateError.length > 1;

      if (ctrl.model.email.enableCustomSendGridEmail) {
        if (ctrl.isProd()) {
          validators.push({
            field: 'sendgrid-production-api-key',
            len: {
              message:
                errorType !== ACCOUNT_ADMINISTRATION_ERROR_CODES.SENDGRID_AUTH_FAILURE.type
                  ? ACCOUNT_ADMINISTRATION_SENDGRID_INVALID_KEY
                  : ACCOUNT_ADMINISTRATION_ERROR_CODES[error.type].error,
              test: {
                value:
                  errorType !== ACCOUNT_ADMINISTRATION_ERROR_CODES.SENDGRID_AUTH_FAILURE.type
                    ? ctrl.model.email.sendGridProductionKey
                    : '',
              },
            },
          });
        }

        if (!ctrl.isProd()) {
          validators.push({
            field: 'sendgrid-sandbox-api-key',
            len: {
              message:
                errorType !== ACCOUNT_ADMINISTRATION_ERROR_CODES.SENDGRID_AUTH_FAILURE.type
                  ? 'Invalid API Key'
                  : ACCOUNT_ADMINISTRATION_ERROR_CODES[error.type].error,
              test: {
                value:
                  errorType !== ACCOUNT_ADMINISTRATION_ERROR_CODES.SENDGRID_AUTH_FAILURE.type
                    ? ctrl.model.email.sendGridSandboxKey
                    : '',
              },
            },
          });
        }
      }

      if (isTemplateError) {
        templateId = templateError[1];

        switch (errorType) {
          case ACCOUNT_ADMINISTRATION_ERROR_CODES.EMAIL_TEMPLATE_ID_DNE.type:
            templateErrorMessage = ACCOUNT_ADMINISTRATION_ERROR_CODES.EMAIL_TEMPLATE_ID_DNE.error;
            break;
          case ACCOUNT_ADMINISTRATION_ERROR_CODES.EMAIL_TYPE_NOT_UNIQUE.type:
            templateErrorMessage = ACCOUNT_ADMINISTRATION_ERROR_CODES.EMAIL_TYPE_NOT_UNIQUE.error;
            break;
          default:
            templateErrorMessage = templateError[0];
            break;
        }
      }

      ctrl.emailTemplates.forEach((item) => {
        const externalTemplateId =
          templateId === item.templateType || templateId == item.id ? '' : item.externalTemplateId;

        if (item.emailType.id === 'custom') {
          validators.push({
            field: 'sendgrid-external-template-id-' + item.templateType,
            len: {
              message: templateErrorMessage,
              test: {
                value: externalTemplateId,
              },
            },
          });
        }
      });

      return validators;
    },

    /**
     * Returns `null` if there are no errors
     */
    getSendGridApiKeysErrors: function (
      errorList: { code: number; error: string }[],
    ): Record<string, { message: string }> | null {
      if (!ctrl.model.email.enableCustomSendGridEmail) {
        return null;
      }

      const sendgridErrorCodesToFields = {
        5183: ctrl.fieldKeys.sendgridProductionApiKey,
        5184: ctrl.fieldKeys.sendgridSandboxApiKey,
      };

      let hasErrors = false;
      const errorHash = {};

      errorList.forEach((apiError) => {
        const field = sendgridErrorCodesToFields[apiError.code];

        if (field) {
          errorHash[field] = { message: apiError.error };
          hasErrors = true;
        }
      });

      return hasErrors ? errorHash : null;
    },

    resetEmailTemplateNames: function () {
      ctrl.emailTemplates.forEach((template) => {
        template.templateName = '';
      });
    },

    processEmailTemplates: function (payload) {
      const validators: TODO[] = [];

      ctrl.emailTemplates.forEach((template) => {
        payload.forEach((item) => {
          if (template.templateType === item.templateType) {
            template.id = item.id;

            if (item.name && ACCOUNT_ADMINISTRATION_SENDGRID_ERRORS.includes(item.name)) {
              validators.push({
                field: 'sendgrid-external-template-id-' + item.templateType,
                len: {
                  message: item.name,
                  test: {
                    value: '',
                  },
                },
              });
            } else if (item.name) {
              template.templateName = item.name;
            }
          }
        });
      });

      if (validators.length && !ValidateService.validate(validators)) {
        ctrl.errors = ValidateService.getErrorHash();
      } else {
        ctrl.errors = false;
      }
    },

    isValid: function (item) {
      return !(ctrl.errors && ctrl.errors.hasOwnProperty(item));
    },

    getErrorMessage: function (item: string): string {
      if (item === 'security-password-minimum-length') {
        return ctrl.validatePasswordMinimumLengthErrors()?.join(', ') ?? '';
      }

      if (ctrl.errors && ctrl.errors.hasOwnProperty(item)) {
        return ctrl.errors[item].message;
      }

      return '';
    },

    updateCCVSettings: function (settings: CCValidationSettings) {
      return accountAdministrationService.updateCCVSettings(ctrl.ccValidationSettings, settings);
    },

    getCCVSettings: function () {
      return accountAdministrationService.getCCVSettings(ctrl.ccValidationSettings);
    },

    marshall: function () {
      let processed = ctrl.cache;

      const passwordPolicy = createPasswordPolicy(ctrl.model.security);
      ctrl.model.security.passwordSecurityPolicyEnabled = passwordPolicy.passwordSecurityPolicyEnabled;
      ctrl.model.security.passwordMinimumLength = passwordPolicy.passwordMinimumLength;
      ctrl.model.security.passwordUseMixedCase = passwordPolicy.passwordUseMixedCase;
      ctrl.model.security.passwordMinimumDigits = passwordPolicy.passwordMinimumDigits;
      ctrl.model.security.passwordMinimumSymbols = passwordPolicy.passwordMinimumSymbols;
      ctrl.model.security.sessionExpiryInMillis = ctrl.getSessionTimeoutInMS();

      ctrl.model.payment.creditCardValidationSettings = ctrl.getCCVSettings();
      ctrl.convertPaymentSettings();
      ctrl.model.b2b.lockExpiryInSeconds = ctrl.getLockExpirationInSeconds();

      if (!ctrl.model.web.membership) {
        ctrl.model.web.membershipVerificationLastName = false as TODO;
      }

      if (ctrl.saveType === 'all') {
        processed = angular.extend(
          { id: (ctrl.model as TODO).id },
          ctrl.model.web,
          ctrl.model.b2b,
          ctrl.model.payment,
          ctrl.model.pos,
          ctrl.model.ac,
          ctrl.model.finance,
          ctrl.model.security,
          ctrl.model.printers,
          ctrl.model.email,
          ctrl.model.analytics,
          ctrl.model.integrations,
          { featuresConfig: ctrl.model.featuresConfig },
        );
      } else if (ctrl.model.hasOwnProperty(ctrl.saveType)) {
        processed = angular.extend(ctrl.cache, ctrl.model[ctrl.saveType]);
      }
      return processed;
    },

    sendGridVerifyMarshall: function () {
      const processed = ctrl.marshall();
      const processedEmailTemplates = ctrl.emailTemplatesMarshall(processed.emailTemplates);

      return {
        sandboxApiKey: processed.sendGridSandboxKey,
        prodApiKey: processed.sendGridProductionKey,
        templates: processedEmailTemplates,
      };
    },

    emailTemplatesMarshall: function (emailTemplates: EmailTemplate[]): EmailTemplateMarshall[] {
      return ensureArray(emailTemplates)
        .map((item) => {
          const result: EmailTemplateMarshall = {
            templateType: item.templateType,
            externalTemplateId: item.emailType.id === 'custom' ? item.externalTemplateId : '',
            useAcmeKey: item.sendGridAcount.id === 'acme',
          };

          if (item.id) {
            result.id = item.id;
          }

          return result;
        })
        .filter((item) => item.externalTemplateId.length);
    },

    checkSessionTime: function () {
      let total = 0;

      if (!ctrl.sessionTimeout.days) {
        ctrl.sessionTimeout.days = 0;
      }

      if (!ctrl.sessionTimeout.hours) {
        ctrl.sessionTimeout.hours = 0;
      }

      if (!ctrl.sessionTimeout.minutes) {
        ctrl.sessionTimeout.minutes = 0;
      }

      if (!ctrl.sessionTimeout.seconds) {
        ctrl.sessionTimeout.seconds = 0;
      }

      total =
        parseInt(ctrl.sessionTimeout.days as TODO, 10) +
        parseInt(ctrl.sessionTimeout.hours as TODO, 10) +
        parseInt(ctrl.sessionTimeout.minutes as TODO, 10) +
        parseInt(ctrl.sessionTimeout.seconds as TODO, 10);

      if (total === 0) {
        ctrl.notice.showErrorMessages([{ message: 'Session Timeout not set. A 10 day timeout will be used.' }]);
      } else {
        ctrl.notice.hideErrorMessages();
      }
    },

    checkLockExpirationTime: function () {
      let total = 0;

      if (!ctrl.lockExpiration.hours) {
        ctrl.lockExpiration.hours = 0;
      }

      if (!ctrl.lockExpiration.minutes) {
        ctrl.lockExpiration.minutes = 0;
      }

      total = parseInt(ctrl.lockExpiration.hours, 10) + parseInt(ctrl.lockExpiration.minutes, 10);

      if (total === 0) {
        ctrl.notice.showErrorMessages([{ message: 'Idle timeout is not set. A 4 hour timeout will be used.' }]);
      } else {
        ctrl.notice.hideErrorMessages();
      }
    },

    getSessionTimeoutInMS: function () {
      return accountAdministrationService.getSessionTimeoutInMS(ctrl.sessionTimeout);
    },

    convertSessionTimeoutToMS: function (ms: number) {
      const sessionTimeout = accountAdministrationService.convertSessionTimeoutToMS(ms);
      ctrl.sessionTimeout.seconds = sessionTimeout.seconds;
      ctrl.sessionTimeout.minutes = sessionTimeout.minutes;
      ctrl.sessionTimeout.hours = sessionTimeout.hours;
      ctrl.sessionTimeout.days = sessionTimeout.days;
    },

    getLockExpirationInSeconds: function () {
      let secs = 0;

      secs += ctrl.lockExpiration.minutes * 60;
      secs += ctrl.lockExpiration.hours * 60 * 60;

      return secs;
    },

    convertLockExpirationFromSeconds: function (secs) {
      const minutes = secs / 60;
      const hours = minutes / 60;

      ctrl.lockExpiration.minutes = parseInt((minutes % 60) as TODO, 10);
      ctrl.lockExpiration.hours = parseInt((hours % 24) as TODO, 10);
    },

    setSendGridAcount: function (name) {
      const account = {
        id: 'venue',
        name: name,
      };

      ctrl.sendGridAcount.push(account);
    },

    setEmailTemplates: function () {
      ctrl.emailTemplates = ctrl.templateTypes.map((item) => {
        const emailtemplate = {
          templateType: item.templateType,
          name: item.name,
          externalTemplateId: '',
          useAcmeKey: true,

          emailTemplateType: item.emailTemplateType,
          emailType: ctrl.emailType[0],
          sendGridAcount: ctrl.sendGridAcount[0],
        } as TODO;

        if (EmailTemplatesData && EmailTemplatesData.data.success && EmailTemplatesData.data.payload.list.length) {
          EmailTemplatesData.data.payload.list.forEach((item) => {
            if (emailtemplate.templateType === item.templateType) {
              emailtemplate.id = item.id;
              emailtemplate.externalTemplateId = item.externalTemplateId;
              emailtemplate.useAcmeKey = item.useAcmeKey;

              if (item.externalTemplateId.length) {
                emailtemplate.emailType = ctrl.emailType[1];
              }

              if (!item.useAcmeKey) {
                emailtemplate.sendGridAcount = ctrl.sendGridAcount[1];
              }
            }
          });
        }

        return emailtemplate;
      });
    },

    processFeesData: function (fee: TODO) {
      ctrl.model.fees = {
        id: fee.id,
        active: fee.active,
        name: fee.name,
        percentage: fee.percentage,
        fixed: fee.fixed,
        inputLabel: fee.inputLabel,
        tooltipLabel: fee.tooltipLabel,
        presentmentType: fee.presentmentType,
        refundable: fee.refundable,
      };
    },

    process: function (data: TODO) {
      let tenantSettings = {
        b2b: {
          startDayOfWeek: UserPreferencesService.getStartDayOfWeekString(),
          dateFormat: UserPreferencesService.getDateFormat('shortDate'),
          hourFormat: UserPreferencesService.getHourFormat(),
        },
      };
      const passwordPolicy = createPasswordPolicy(data);
      const model = {
        id: data.id,
        web: {
          membership: data.membership,
          membershipSales: data.membershipSales,
          membershipBenefitsExtension: data.membershipBenefitsExtension,
          membershipVerificationLastName: data.membershipVerificationLastName,
          giftCertificates: data.giftCertificates,
          coupons: data.coupons,
          billingAddress: data.billingAddress,
          sortTemplatesByDisplayOrder: data.sortTemplatesByDisplayOrder,
          reviews: data.reviews,
        },
        b2b: {
          settings: {
            b2b: {},
            web: {},
            pos: {},
            reseller: {},
          },
          b2bBillingAddress: data.b2bBillingAddress,
          lockAlwaysRequiredToUpdateRequiresLockObjects: data.lockAlwaysRequiredToUpdateRequiresLockObjects,
          auxiliaryMembership: data.auxiliaryMembership,
          allowTicketTransactionOnExpiredMemberships: data.allowTicketTransactionOnExpiredMemberships,
          // lockExpiryInSeconds is set below
        },
        featuresConfig: data.featuresConfig, // Depends entirely on BE to return correctly
        payment: {
          allowAMEX: data.allowAMEX,
          amexPaymentProcessor: data.amexPaymentProcessor,
          paymentProcessor: data.paymentProcessor,
          expressURL: data.expressURL,
          expressAccountID: data.expressAccountID,
          expressAccountToken: data.expressAccountToken,
          expressAcceptorID: data.expressAcceptorID,
          expressApplicationID: data.expressApplicationID,
          // Update and return credit card validation settings for interface
          creditCardValidationSettings: ctrl.updateCCVSettings(data.creditCardValidationSettings),
        },
        pos: {
          surveyFrequency: data.surveyFrequency,
          offlinePin: data.offlinePin,
          allowPrintingForExpiredMemberships: data.allowPrintingForExpiredMemberships,
          disableMembershipManage: data.disableMembershipManage,
          enableCustomerInputOnPurchase: data.enableCustomerInputOnPurchase,
          vouchersRequireOrganization: data.vouchersRequireOrganization,
        },
        ac: {
          membershipTicketTypeSelection: data.membershipTicketTypeSelection,
        },
        finance: {
          rounding: data.rounding,
          abbreviatedName: data.abbreviatedName,
        },
        security: {
          passwordSecurityPolicyEnabled: passwordPolicy.passwordSecurityPolicyEnabled,
          passwordMinimumLength: passwordPolicy.passwordMinimumLength,
          passwordUseMixedCase: passwordPolicy.passwordUseMixedCase,
          passwordMinimumDigits: passwordPolicy.passwordMinimumDigits,
          passwordMinimumSymbols: passwordPolicy.passwordMinimumSymbols,
          sessionExpiryInMillis: data.sessionExpiryInMillis || 0,
        },
        printers: {
          printers: data.printers || [],
        },
        email: {
          enableSendGridTracking: data.enableSendGridTracking,
          notificationPreference: data.notificationPreference || ctrl.notificationPreferenceSettings[0].name,
          enableCustomEmail: data.enableCustomEmail,
          enableCustomSendGridEmail: data.sendGridProductionKey || data.sendGridSandboxKey ? true : false,
          sendGridProductionKey: data.sendGridProductionKey || '',
          sendGridSandboxKey: data.sendGridSandboxKey || '',
          emailTemplates: data.emailTemplates || ctrl.emailTemplates,
        },
        analytics: {
          enableGoogleAnalyticsTracking: data.enableGoogleAnalyticsTracking,
          enableGtmTracking: data.enableGtmTracking,
        },
        integrations: {
          externalSystemIntegration: data.externalSystemIntegration,
          enableExternalNotifications: data.enableExternalNotifications,
          allowUpgradeExternalMemberships: data.allowUpgradeExternalMemberships,
          invalidateExtraneousMembershipCards: data.invalidateExtraneousMembershipCards,
        },
      };

      ctrl.sendGridProductionKeyEmpty = model.email.sendGridProductionKey.length === 0;
      ctrl.sendGridSandboxKeyEmpty = model.email.sendGridSandboxKey.length === 0;

      ctrl.vm.maskedSendGridProductionKey = model.email.sendGridProductionKey;
      ctrl.vm.maskedSendGridSandboxKey = model.email.sendGridSandboxKey;

      if (data.sessionExpiryInMillis) {
        ctrl.convertSessionTimeoutToMS(data.sessionExpiryInMillis);
      }

      if (data.settings && data.settings.b2b) {
        tenantSettings = data.settings;
      }

      model.b2b.settings = tenantSettings as TODO;

      if (data.lockExpiryInSeconds) {
        ctrl.convertLockExpirationFromSeconds(data.lockExpiryInSeconds);
      }

      return model;
    },

    isInValidExtenion: function () {
      const pattern = /^\d+$/;
      return !pattern.test(ctrl.model.web.membershipBenefitsExtension as TODO);
    },

    selectLanguages: function () {
      const selectLanguagesModal = $uibModal.open<TODO>({
        templateUrl: 'views/application/views/account/administration/emailLanguage/emailLanguage.tpl.html',
        size: 'md',
        controller: 'AccountAdministrationEmailLanguageController',
        resolve: {
          selectedLanguages: () => ctrl.selectedLanguages,
        },
      });

      selectLanguagesModal.result
        .then((result) => {
          let hasPrimaryLanguage = false;

          ctrl.selectedLanguages = result;

          if (ctrl.selectedLanguages.length) {
            ctrl.selectedLanguages.forEach(function (item) {
              if (item.primary) {
                ctrl.primaryLanguage = item;
                hasPrimaryLanguage = true;
              }
            });

            if (!hasPrimaryLanguage) {
              ctrl.selectedLanguages[0].primary = true;
              ctrl.primaryLanguage = ctrl.selectedLanguages[0];
            }
          } else {
            ctrl.primaryLanguage = false;
          }
        })
        .catch(() => {
          // noop
        });
    },

    updatePrimaryLanguage: function (data) {
      EmailLanguagesService.updatePrimaryLanguage(data);
    },

    showEditSendgridProdAPIKey: function () {
      return ctrl.sendGridProductionKeyEmpty || ctrl.isSendgridProdAPIKeyEdit;
    },

    editSendgridProdAPIKey: function () {
      ctrl.sendGridProductionKey = ctrl.model.email.sendGridProductionKey;
      ctrl.model.email.sendGridProductionKey = '';

      ctrl.isSendgridProdAPIKeyEdit = true;
    },

    cancelSendgridProdAPIKey: function () {
      ctrl.model.email.sendGridProductionKey = ctrl.sendGridProductionKey;
      ctrl.sendGridProductionKey = '';
      ctrl.isSendgridProdAPIKeyEdit = false;
    },

    showEditSendgridSandAPIKey: function () {
      return ctrl.sendGridSandboxKeyEmpty || ctrl.isSendgridSandAPIKeyEdit;
    },

    editSendgridSandAPIKey: function () {
      ctrl.sendGridSandboxKey = ctrl.model.email.sendGridSandboxKey;
      ctrl.model.email.sendGridSandboxKey = '';

      ctrl.isSendgridSandAPIKeyEdit = true;
    },

    cancelSendgridSandAPIKey: function () {
      ctrl.model.email.sendGridSandboxKey = ctrl.sendGridSandboxKey;
      ctrl.sendGridSandboxKey = '';

      ctrl.isSendgridSandAPIKeyEdit = false;
    },

    processUpdateResponse: function (response: TODO) {
      const payload = response && response.data && response.data.payload;

      const tenantSettings = payload.settings || {};
      const sendGridProductionKey = payload.sendGridProductionKey || '';
      const sendGridSandboxKey = payload.sendGridSandboxKey || '';

      UserPreferencesService.process(tenantSettings.b2b);

      if (sendGridProductionKey.length === 0) {
        ctrl.sendGridProductionKeyEmpty = true;
        ctrl.isSendgridProdAPIKeyEdit = false;
      } else {
        ctrl.sendGridProductionKeyEmpty = false;
        ctrl.isSendgridProdAPIKeyEdit = false;
      }

      if (sendGridSandboxKey.length === 0) {
        ctrl.sendGridSandboxKeyEmpty = true;
        ctrl.isSendgridSandAPIKeyEdit = false;
      } else {
        ctrl.sendGridSandboxKeyEmpty = false;
        ctrl.isSendgridSandAPIKeyEdit = false;
      }

      ctrl.vm.maskedSendGridProductionKey = sendGridProductionKey;
      ctrl.vm.maskedSendGridSandboxKey = sendGridSandboxKey;
    },

    /**
     * Our API error messages are almost always strings but we can also receive a stringified array
     * in some cases (e.g. errors in both prod and sandbox sengrid api keys)
     *
     * @param {*} maybeJSON
     *
     * @note `valid` means if the input was a valid JSON
     */
    tryParseJSON: function (maybeJSON) {
      try {
        return { valid: true, value: JSON.parse(maybeJSON) };
      } catch (error) {
        return { valid: false, value: maybeJSON };
      }
    },

    normalizeErrorDataFromResponse: function (response) {
      const errorData = response.data?.errorData;
      if (!errorData) return [];
      const errorDataMessage = ctrl.tryParseJSON(errorData.message);
      const errorCode = errorData.errorCode;

      let normalizedErrorDataList: TODO[] = [];

      /**
       * If errorCode is `-1` it's most likely a stringified array error message
       */
      if (errorCode === -1 && errorDataMessage.valid) {
        normalizedErrorDataList = errorDataMessage.value;
      } else {
        normalizedErrorDataList = [{ error: errorData.message, code: errorData.errorCode }];
      }

      return normalizedErrorDataList;
    },

    success: {
      init: function (response) {
        ctrl.cache = response.data.payload;
        ctrl.model = ctrl.process(response.data.payload) as TODO;
      },
      update: function (response) {
        ctrl.processUpdateResponse(response);

        ctrl.notice.showSuccessMessages([{ message: 'Successfully updated settings' }], { duration: 3000 });

        // next tick
        $timeout(() => {
          $scope.$emit('Application::tenantSettingsUpdate');
        }, 0);
      },

      verify: function (response) {
        if (response.data.payload.length) {
          ctrl.processEmailTemplates(response.data.payload);
        }

        ctrl.notice.hideErrorMessages();
        ctrl.notice.showSuccessMessages([{ message: 'Successfully verified SendGrid settings' }], { duration: 3000 });
      },

      emailtemplates: function (res) {
        if (res.data.payload.length && (!ctrl.sendGridProductionKeyEmpty || !ctrl.sendGridSandboxKeyEmpty)) {
          ctrl.processEmailTemplates(res.data.payload);
        }
      },

      // TODO: fix typo
      setCustomLanguags: function (res) {
        ctrl.selectedLanguages = res.data.payload;
        let hasPrimaryLanguage = false;
        if (ctrl.selectedLanguages.length) {
          ctrl.selectedLanguages.forEach(function (item) {
            if (item.primary === true) {
              hasPrimaryLanguage = true;
              ctrl.primaryLanguage = item;
            }
          });

          if (!hasPrimaryLanguage) {
            ctrl.selectedLanguages[0].primary = true;
            ctrl.primaryLanguage = ctrl.selectedLanguages[0];
          }
        } else {
          ctrl.primaryLanguage = false;
        }
      },

      updateSelectedLanguage: function () {},
    },

    error: {
      init: function (response) {},

      update: function (response) {
        const error = ctrl.tryParseJSON(response.data.error);

        const normalizedErrorList = error.valid ? error.value : [{ error: error.value }];
        const normalizedErrorDataList = ctrl.normalizeErrorDataFromResponse(response);
        const sendgridErrors = ctrl.getSendGridApiKeysErrors(normalizedErrorDataList);

        UserPreferencesService.process();

        if (sendgridErrors) {
          const baseErrors = !!ctrl.errors ? ctrl.errors : {};
          ctrl.errors = Object.assign(baseErrors, sendgridErrors);

          ctrl.notice.showErrorMessages([{ message: ACCOUNT_ADMINISTRATION_SENDGRID_COMMON_MESSAGE }]);
        } else {
          const errorMessage = normalizedErrorList.map((item) => item.error).join(', ');
          ctrl.notice.showErrorMessages([{ message: errorMessage }]);
        }
      },

      emailtemplates: function (response) {
        const error = response.data.errorData;

        if (ACCOUNT_ADMINISTRATION_ERROR_CODES.hasOwnProperty(error.type)) {
          UserPreferencesService.process();
          ctrl.notice.showErrorMessages([{ message: ACCOUNT_ADMINISTRATION_SENDGRID_COMMON_MESSAGE }]);

          ValidateService.validate(ctrl.getValidators(error));
          ctrl.errors = ValidateService.getErrorHash();

          if (ctrl.errors.hasOwnProperty('sendgrid-production-api-key')) {
            ctrl.sendGridProductionKeyEmpty = true;
          }

          if (ctrl.errors.hasOwnProperty('sendgrid-sandbox-api-key')) {
            ctrl.sendGridSandboxKeyEmpty = true;
          }
        } else {
          UserPreferencesService.process();
          ctrl.notice.showErrorMessages([{ message: response.data.error }]);
        }
      },

      setCustomLanguags: function (res) {},

      updateSelectedLanguage: function () {},
    },
  };

  $scope.accountAdministration = ctrl;
  ctrl.init();
}

export const AccountAdministrationControllerModule = angular
  .module('acme.view.application.account.administration.controllers', [
    uiRouter,
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.service.permissions',
    'acme.common.factory.notice',
    'acme.common.service.userPreferences',
    CapitalizeFirstLetterFilterModule,
    'acme.common.service.themesHelper',
    'acme.common.service.validate',
    AuthenticationServiceModule,
    AccountAdministrationServiceModule,
    'acme.view.application.account.administration.emailLanguage.controllers',
    NoticesComponentModule,
  ])
  .controller('AccountAdministrationController', AccountAdministrationController).name;
